<template>
  <div v-if="isLendflowUser" class="flex flex-wrap items-center space-x-1">
    <div
      v-for="item in lendflowUsersMapping"
      :key="item.type"
      v-tooltip="item.tooltip"
      class="flex flex-wrap items-center"
    >
      <assign-user-dropdown
        :model-value="item.model"
        :app-id="activeDeal.id"
        load-options-when-open
        :type="item.type"
        is-extended
        avatar
      />
      <div
        class="absolute -right-[4px] -bottom-[5px] flex h-5 w-5 items-center justify-center rounded-full border-3 border-white bg-lendflow-green p-[2px] text-black"
      >
        <icon-base
          :icon="IconLogo"
          width="10"
          height="10"
          view-box="0 0 28 28"
          fill="black"
        />
      </div>
    </div>
  </div>
  <div
    v-if="isLendflowUser || isClientWithPermission"
    class="flex flex-wrap items-center"
  >
    <div
      v-for="item in clientUsersMapping"
      :key="item.type"
      v-tooltip="item.tooltip"
      class="flex items-center justify-center"
    >
      <assign-user-dropdown
        :model-value="item.model"
        :client-id="item.clientId"
        :app-id="activeDeal.id"
        :type="item.type"
        load-options-when-open
        is-extended
        avatar
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { formatUserFullName } from "@/helpers/formatting";
import { useAuth } from "@/hooks/auth";
import { useDealsBase } from "@/hooks/deals";

import IconLogo from "@/components/icons/IconLogo.vue";
import AssignUserDropdown from "@/views/deals/components/AssignUserDropdown.vue";

import type { IClient } from "@/models/clients";

const { getters } = useStore();
const { activeDeal } = useDealsBase();
const { t } = useI18n();

const {
  isLendflowUser,
  isClientAdmin,
  isClientUnderwriter,
  isClientAnalyst,
  isClientFundingAdvisor,
} = useAuth();

const client = computed<IClient | null>(
  () => getters["auth/authClientSettings"]
);

const isClientWithPermission = computed(() => {
  const clientCanCreateUnderwriter = !!client.value?.can_create_underwriter;
  return (
    (isClientAdmin ||
      isClientUnderwriter ||
      isClientAnalyst ||
      isClientFundingAdvisor) &&
    clientCanCreateUnderwriter
  );
});

const currentUnderwriter = computed(() =>
  activeDeal.value.underwriter
    ? formatUserFullName(activeDeal.value.underwriter)
    : ""
);
const currentFundingAdvisor = computed(() =>
  activeDeal.value.funding_advisor
    ? formatUserFullName(activeDeal.value.funding_advisor)
    : ""
);
const currentAnalyst = computed(() =>
  activeDeal.value.analyst ? formatUserFullName(activeDeal.value.analyst) : ""
);
const currentClientUnderwriter = computed(() =>
  activeDeal.value.client_underwriter
    ? formatUserFullName(activeDeal.value.client_underwriter)
    : ""
);
const currentClientFundingAdvisor = computed(() =>
  activeDeal.value.client_funding_advisor
    ? formatUserFullName(activeDeal.value.client_funding_advisor)
    : ""
);
const currentClientAnalyst = computed(() =>
  activeDeal.value.client_analyst
    ? formatUserFullName(activeDeal.value.client_analyst)
    : ""
);

const lendflowUsersMapping = computed(
  () =>
    [
      {
        type: "underwriter",
        model: currentUnderwriter.value,
        tooltip: `${t("COMMON.LENDFLOW")} ${t(
          "DEALS.TABLE_HEADERS.UNDERWRITER"
        )}: ${currentUnderwriter.value || t("COMMON.UNASSIGNED")}`,
      },
      {
        type: "fundingAdvisor",
        model: currentFundingAdvisor.value,
        tooltip: `${t("COMMON.LENDFLOW")} ${t(
          "DEALS.TABLE_HEADERS.FUNDING_ADVISOR"
        )}: ${currentFundingAdvisor.value || t("COMMON.UNASSIGNED")}`,
      },
      {
        type: "analyst",
        model: currentAnalyst.value,
        tooltip: `${t("COMMON.LENDFLOW")} ${t("DEALS.TABLE_HEADERS.ANALYST")}: ${
          currentAnalyst.value || t("COMMON.UNASSIGNED")
        }`,
      },
    ] as const
);

const clientUsersMapping = computed(
  () =>
    [
      {
        type: "clientUnderwriter",
        model: currentClientUnderwriter.value,
        clientId: activeDeal.value.client?.id,
        tooltip: `${t("DEALS.TABLE_HEADERS.UNDERWRITER")}: ${
          currentClientUnderwriter.value || t("COMMON.UNASSIGNED")
        }`,
      },
      {
        type: "clientFundingAdvisor",
        model: currentClientFundingAdvisor.value,
        clientId: activeDeal.value.client?.id,
        tooltip: `${t("DEALS.TABLE_HEADERS.FUNDING_ADVISOR")}: ${
          currentClientFundingAdvisor.value || t("COMMON.UNASSIGNED")
        }`,
      },
      {
        type: "clientAnalyst",
        model: currentClientAnalyst.value,
        clientId: activeDeal.value.client?.id,
        tooltip: `${t("DEALS.TABLE_HEADERS.ANALYST")}: ${
          currentClientAnalyst.value || t("COMMON.UNASSIGNED")
        }`,
      },
    ] as const
);
</script>
